import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  TrendingUp,
  Package,
  Boxes,
  AlertTriangle,
  Factory,
  MapPin,
  Truck,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";

function Kpi({ label, value, sub, icon: Icon, accent, blink, testId }) {
  return (
    <div
      className={`bg-white border p-5 ${blink ? "border-orange-500 ring-2 ring-orange-200 animate-pulse" : "border-zinc-300"}`}
      data-testid={testId || `kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          {label}
        </div>
        <div className={`h-7 w-7 flex items-center justify-center ${accent || "bg-zinc-900"}`}>
          <Icon size={14} className="text-white" />
        </div>
      </div>
      <div className="text-3xl font-mono font-semibold text-zinc-900 mt-3">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1 font-mono">{sub}</div>}
    </div>
  );
}

function ChartCard({ title, sub, icon: Icon, children, testId }) {
  return (
    <div className="bg-white border border-zinc-300 p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{sub}</div>
          <h3 className="text-lg font-semibold text-zinc-900">{title}</h3>
        </div>
        <Icon size={18} className="text-orange-600" />
      </div>
      <div className="h-56">{children}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const role = user?.role;
  // Per-role widget visibility
  const showSalesCharts = role !== "production";              // production: no sales graphs
  const showProductionCharts = role !== "sales";              // sales: no production output graphs
  const showLowStock = role !== "sales";                      // sales: no low-stock items panel
  const showBatchPrintBtn = role !== "sales";                 // sales: no "Print all open work orders" button
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) {
    return <div className="text-zinc-500 font-mono text-xs">LOADING METRICS...</div>;
  }

  const lowStockBlink = (stats.low_stock_count || 0) > 0;
  const kani = stats.by_location?.kani || { cartons: 0, net_kg: 0, gross_kg: 0 };
  const naroda = stats.by_location?.naroda || { cartons: 0, net_kg: 0, gross_kg: 0 };

  const fmtMonth = (m) => {
    const [y, mm] = (m || "").split("-");
    if (!y || !mm) return m;
    const d = new Date(parseInt(y), parseInt(mm) - 1, 1);
    return d.toLocaleString("en-IN", { month: "short", year: "2-digit" });
  };
  const sixmProd = (stats.sixm_production || []).map((r) => ({ ...r, label: fmtMonth(r.month) }));
  const sixmSales = (stats.sixm_sales || []).map((r) => ({ ...r, label: fmtMonth(r.month) }));

  // Personalized greeting line per role
  const hour = new Date().getHours();
  const partOfDay = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const userName = (user?.name || user?.email || "").split("@")[0].split(/\s+/)[0] || "there";
  const fname = userName.charAt(0).toUpperCase() + userName.slice(1);
  const todayKg = stats.today.net_kg || 0;
  const yKg = stats.yesterday?.net_kg || 0;
  const delta = yKg > 0 ? Math.round(((todayKg - yKg) / yKg) * 100) : null;
  const deltaLabel = delta === null ? (todayKg > 0 ? "first run today" : "no yesterday data") : `${delta >= 0 ? "+" : ""}${delta}% vs yesterday`;
  const deltaColor = delta === null ? "text-zinc-400" : delta >= 0 ? "text-orange-400" : "text-zinc-300";
  const greetingLines = {
    production: { headline: `${todayKg.toFixed(1)} kg`, sub: `today's net · ${deltaLabel}`, accent: deltaColor },
    sales:      { headline: `${stats.pending_orders} pending orders`, sub: `${stats.dispatch_ready_cartons || 0} cartons ready for dispatch`, accent: "text-orange-400" },
    dispatch:   { headline: `${stats.dispatch_ready_cartons || 0} cartons`, sub: "assigned & ready to ship from your floor", accent: "text-orange-400" },
    admin:      { headline: `${todayKg.toFixed(1)} kg · ${stats.pending_orders} orders · ${stats.dispatch_ready_cartons || 0} ready`, sub: `${stats.low_stock_count} stock alert${stats.low_stock_count === 1 ? "" : "s"}`, accent: stats.low_stock_count > 0 ? "text-orange-400" : "text-zinc-300" },
  };
  const g = greetingLines[role] || greetingLines.admin;

  return (
    <div className="space-y-6">
      {/* Personalized greeting strip per role */}
      <div
        className="bg-zinc-950 text-white border-l-4 border-orange-600 px-5 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-1"
        data-testid="role-greeting"
      >
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{partOfDay}, {fname} · {role}</div>
          <div className="font-semibold text-lg tracking-tight mt-0.5" data-testid="greeting-headline">{g.headline}</div>
        </div>
        <div className={`text-[11px] font-mono uppercase tracking-widest ${g.accent.replace("text-zinc-", "text-zinc-3").replace("text-orange-700", "text-orange-400")}`} data-testid="greeting-sub">
          {g.sub}
        </div>
      </div>

      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
            Live Operations · {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "short", year: "numeric" })}
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-950">Control Dashboard</h1>
        </div>
        {showBatchPrintBtn && (
          <a
            href="/work-orders/batch-print"
            target="_blank"
            rel="noreferrer"
            data-testid="batch-work-orders"
            className="text-[11px] uppercase tracking-wider font-mono px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white inline-flex items-center gap-2"
          >
            🖨 Print all open work orders
          </a>
        )}
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Today Production" value={`${stats.today.net_kg?.toFixed(1) || 0} kg`} sub={`${stats.today.cartons} cartons · ${(stats.today.gross_kg || 0).toFixed(1)} kg gross`} icon={Factory} accent="bg-orange-600" />
        <Kpi label="Pending Orders" value={stats.pending_orders} sub={`${stats.dispatched_today} dispatched`} icon={Package} accent="bg-zinc-800" />
        <Kpi label="Finished Stock" value={stats.fg_total_cartons} sub={`${stats.products_count} categories · cartons in stock`} icon={Boxes} accent="bg-zinc-600" />
        <Kpi label="Low Stock Alerts" value={stats.low_stock_count} sub={stats.low_stock_count > 0 ? "Action needed" : "All stock OK"} icon={AlertTriangle} accent={lowStockBlink ? "bg-orange-600" : "bg-zinc-400"} blink={lowStockBlink} testId="kpi-low-stock" />
      </div>

      {/* Per-location stock tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border-2 border-orange-300 p-5" data-testid="loc-kani">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-orange-700" />
              <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Plant · Kani</div>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Finished Stock</div>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-4">
            <div><div className="text-3xl font-mono font-semibold text-zinc-900">{kani.cartons}</div><div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">cartons</div></div>
            <div><div className="text-xl font-mono font-semibold text-zinc-700">{kani.net_kg.toFixed(0)}</div><div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">net kg</div></div>
            <div><div className="text-xl font-mono font-semibold text-orange-700">{kani.gross_kg.toFixed(0)}</div><div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">gross kg</div></div>
          </div>
        </div>
        <div className="bg-white border-2 border-zinc-300 p-5" data-testid="loc-naroda">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-zinc-700" />
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-700">Plant · Naroda</div>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Finished Stock</div>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-4">
            <div><div className="text-3xl font-mono font-semibold text-zinc-900">{naroda.cartons}</div><div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">cartons</div></div>
            <div><div className="text-xl font-mono font-semibold text-zinc-700">{naroda.net_kg.toFixed(0)}</div><div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">net kg</div></div>
            <div><div className="text-xl font-mono font-semibold text-orange-700">{naroda.gross_kg.toFixed(0)}</div><div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">gross kg</div></div>
          </div>
        </div>
      </div>

      {/* 4-chart grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {showProductionCharts && (
        <ChartCard title="Production Output" sub="Last 7 Days · Net kg" icon={TrendingUp} testId="chart-prod-7d">
          {(stats.week_trend || []).length === 0 ? (
            <Empty label="No production in last 7 days" />
          ) : (
            <ResponsiveContainer>
              <LineChart data={stats.week_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#71717a" />
                <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#71717a" />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="kg" stroke="#ea580c" strokeWidth={2} dot={{ fill: "#ea580c", r: 3 }} name="Net kg" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        )}

        {showProductionCharts && (
        <ChartCard title="Production Output" sub="Last 6 Months · Net kg" icon={Factory} testId="chart-prod-6m">
          {sixmProd.length === 0 ? (
            <Empty label="No production in last 6 months" />
          ) : (
            <ResponsiveContainer>
              <BarChart data={sixmProd}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#71717a" />
                <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#71717a" />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="kg" fill="#ea580c" name="Net kg" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        )}

        {showSalesCharts && (
        <ChartCard title="Sales / Dispatch" sub="Last 7 Days · Net kg" icon={Truck} testId="chart-sales-7d">
          {(stats.week_sales || []).length === 0 ? (
            <Empty label="No dispatches in last 7 days" />
          ) : (
            <ResponsiveContainer>
              <LineChart data={stats.week_sales}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#71717a" />
                <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#71717a" />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="kg" stroke="#27272a" strokeWidth={2} dot={{ fill: "#27272a", r: 3 }} name="Net kg" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        )}

        {showSalesCharts && (
        <ChartCard title="Sales / Dispatch" sub="Last 6 Months · Net kg" icon={Truck} testId="chart-sales-6m">
          {sixmSales.length === 0 ? (
            <Empty label="No dispatches in last 6 months" />
          ) : (
            <ResponsiveContainer>
              <BarChart data={sixmSales}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#71717a" />
                <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#71717a" />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="kg" fill="#27272a" name="Net kg" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        )}
      </div>

      {/* Low stock card */}
      {showLowStock && (
      <div className="bg-white border border-zinc-300 p-5" data-testid="low-stock-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Alerts</div>
            <h3 className="text-lg font-semibold text-zinc-900">Low Stock Items</h3>
          </div>
          {lowStockBlink && <span className="text-[10px] font-mono uppercase tracking-widest text-white bg-orange-600 px-2 py-1 animate-pulse">{stats.low_stock_count} ITEMS NEED ACTION</span>}
        </div>
        {stats.low_stock_items.length === 0 ? (
          <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 p-4 text-center">All inward & finished stock above reorder level</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {stats.low_stock_items.map((m) => (
              <div key={m.id} className="flex items-center justify-between border border-orange-200 bg-orange-50 px-3 py-2" data-testid={`low-stock-${m.id}`}>
                <div>
                  <div className="text-sm font-medium text-zinc-900">{m.name}</div>
                  <div className="text-[10px] font-mono uppercase text-orange-700">{m.kind === "finished" ? "Finished Goods" : (m.grade || "Inward")}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm text-orange-700">{m.stock} {m.unit}</div>
                  <div className="text-[10px] font-mono text-zinc-500">limit {m.reorder_level}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function Empty({ label }) {
  return <div className="h-full flex items-center justify-center text-sm text-zinc-500 border border-dashed border-zinc-300">{label}</div>;
}

const tooltipStyle = { fontSize: 12, fontFamily: "IBM Plex Mono", border: "1px solid #d4d4d8", borderRadius: 0 };
